import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MdExpandMore } from 'react-icons/md';

function summarizeSelection(values, options, placeholder) {
  if (!values?.length) return placeholder;
  const labels = values
    .map((v) => options.find((o) => String(o.value) === String(v))?.label)
    .filter(Boolean);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.length} selected`;
}

export function AppMultiSelect({
  label,
  options = [],
  values = [],
  onChange,
  placeholder = 'Select…',
  helperText,
  h = '40px',
  labelFontSize,
  isDisabled = false,
  minW,
}) {
  const summary = useMemo(
    () => summarizeSelection(values, options, placeholder),
    [values, options, placeholder],
  );

  const toggle = (optionValue) => {
    const key = String(optionValue);
    const set = new Set((values || []).map(String));
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onChange?.([...set]);
  };

  const selectAll = () => onChange?.(options.map((o) => o.value));
  const clearAll = () => onChange?.([]);

  return (
    <FormControl isDisabled={isDisabled} minW={minW}>
      {label ? (
        <FormLabel fontSize={labelFontSize} mb={2}>
          {label}
        </FormLabel>
      ) : null}
      <Popover
        placement="bottom-start"
        strategy="fixed"
        gutter={6}
        isLazy
        flip
        matchWidth
      >
        <PopoverTrigger>
          <Button
            type="button"
            variant="unstyled"
            w="100%"
            h={h}
            minH={h}
            px={3}
            fontWeight="400"
            fontSize="14px"
            bg="bg.input"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="sm"
            color={values?.length ? 'text.primary' : 'text.placeholder'}
            _hover={{ bg: 'bg.input', borderColor: 'border.subtle' }}
            _active={{ bg: 'bg.input' }}
            _focus={{
              bg: 'bg.input',
              borderColor: 'utkarsh.amber',
              boxShadow: '0 0 0 1px #F9B101',
            }}
            isDisabled={isDisabled}
          >
            <Box
              as="span"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              w="100%"
              gap={2}
            >
              <Text as="span" flex="1" minW={0} textAlign="left" noOfLines={1}>
                {summary}
              </Text>
              <Box as="span" display="inline-flex" flexShrink={0} color="text.secondary">
                <MdExpandMore size={20} />
              </Box>
            </Box>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          bg="bg.surface"
          borderColor="border.subtle"
          borderRadius="md"
          boxShadow="lg"
          zIndex={2000}
          _focus={{ outline: 'none' }}
        >
          <PopoverBody p={2} maxH="240px" overflowY="auto">
            <VStack align="stretch" spacing={0}>
              <Button
                size="xs"
                variant="ghost"
                justifyContent="flex-start"
                fontWeight="500"
                color="text.secondary"
                onClick={selectAll}
              >
                Select all
              </Button>
              <Button
                size="xs"
                variant="ghost"
                justifyContent="flex-start"
                fontWeight="500"
                color="text.secondary"
                mb={1}
                onClick={clearAll}
              >
                Clear all
              </Button>
              {options.map((o) => {
                const checked = (values || []).map(String).includes(String(o.value));
                return (
                  <Box
                    key={String(o.value)}
                    px={2}
                    py={2}
                    borderRadius="sm"
                    bg={checked ? 'rgba(249, 177, 1, 0.12)' : 'transparent'}
                    _hover={{ bg: 'rgba(249, 177, 1, 0.18)' }}
                    cursor="pointer"
                    onClick={() => toggle(o.value)}
                  >
                    <Checkbox
                      isChecked={checked}
                      pointerEvents="none"
                      colorScheme="yellow"
                      size="sm"
                    >
                      <Text fontSize="14px" fontWeight={checked ? '600' : '500'}>
                        {o.label}
                      </Text>
                    </Checkbox>
                  </Box>
                );
              })}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {helperText ? (
        <FormHelperText color="text.secondary">{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
